/**
 * 관리자 > 접속 통계 — 차트 렌더링 컨트롤러.
 *
 * data-action 값에 따라 탭별 차트를 렌더한다:
 *   dashboard   → 성과 대시보드  (추이 라인 + 채널 도넛 + 전환 이벤트 막대)
 *   acquisition → 유입 분석      (채널 도넛 + 소스 가로막대)
 *   audience    → 고객 분석      (신규/재방문 도넛[중앙텍스트] + 디바이스 도넛 + 브라우저 막대)
 *   marketing   → 마케팅 인사이트 (추이 라인 + 채널 도넛 + 전환 이벤트 막대)
 *   index       → 개발자 데이터  (기존 전체 차트)
 *
 * Chart.js 4.4.x CDN UMD 로드 전제.
 */
import { Logger } from "../../utils/Logger";

declare const Chart: any;

// ── 타입 ──────────────────────────────────────────────────────────────────
interface TrendBlock {
  granularity: string;
  labels: string[];
  visitors: number[];
  sessions: number[];
  pageviews: number[];
  conversions: number[];
}
interface ChannelPerfRow  { channel: string; visitors: number; conversions: number; share_pct: number; }
interface SourceRow       { source: string; sessions: number; }
interface NamedCount      { event_name?: string; event_label?: string; count?: number; }
interface BreakdownRow    { [k: string]: any; sessions: number; }
interface NewReturnData   { new_visitors?: number; returning?: number; }

interface AnalyticsBootstrap {
  // 대시보드
  trend?:       TrendBlock;
  channelPerf?: ChannelPerfRow[];
  conversions?: NamedCount[];
  // 유입 분석
  sources?:     SourceRow[];
  // 고객 분석
  devices?:     BreakdownRow[];
  browsers?:    BreakdownRow[];
  os?:          BreakdownRow[];
  // 개발자 (legacy)
  channels?:    { channel: string; sessions: number }[];
}

// ── 상수 ──────────────────────────────────────────────────────────────────
const CTRL = "AdminAnalyticsController";

const PALETTE = [
  "#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6",
  "#ec4899","#14b8a6","#f97316","#6366f1","#84cc16",
  "#06b6d4","#a855f7",
];

const CHANNEL_LABELS: Record<string, string> = {
  direct:   "직접 방문",
  organic:  "검색",
  social:   "SNS",
  paid:     "광고",
  campaign: "캠페인",
  referral: "외부 링크",
  internal: "내부 이동",
  email:    "이메일",
};

const DEVICE_LABELS: Record<string, string> = {
  desktop: "PC",
  mobile:  "모바일",
  tablet:  "태블릿",
  unknown: "기타",
};

const INK   = "#0a0a0a";
const MUTED = "#737373";
const GRID  = "rgba(0,0,0,0.06)";

// ── 공통 유틸 ─────────────────────────────────────────────────────────────
function applyDefaults(): void {
  if (typeof Chart === "undefined" || !Chart.defaults) return;
  Chart.defaults.font.family =
    "'Pretendard','Apple SD Gothic Neo',system-ui,-apple-system,'Segoe UI',Roboto,sans-serif";
  Chart.defaults.font.size = 12;
  Chart.defaults.color     = MUTED;
  Chart.defaults.plugins.legend.labels.usePointStyle = true;
  Chart.defaults.plugins.legend.labels.boxWidth      = 8;
  Chart.defaults.plugins.legend.labels.padding       = 14;
  Chart.defaults.plugins.tooltip.backgroundColor     = INK;
  Chart.defaults.plugins.tooltip.padding             = 10;
  Chart.defaults.plugins.tooltip.cornerRadius        = 8;
  Chart.defaults.plugins.tooltip.titleColor          = "#fff";
  Chart.defaults.plugins.tooltip.bodyColor           = "#e5e5e5";
  Chart.defaults.plugins.tooltip.boxPadding          = 4;
  if (Chart.defaults.scale?.grid)   Chart.defaults.scale.grid.color          = GRID;
  if (Chart.defaults.scale?.border) Chart.defaults.scale.border.display      = false;
}

function readBootstrap(): AnalyticsBootstrap | null {
  const el = document.getElementById("analytics-bootstrap");
  if (!el?.textContent) return null;
  try { return JSON.parse(el.textContent) as AnalyticsBootstrap; }
  catch (e) { Logger.debug(`${CTRL} bootstrap parse fail`); return null; }
}

function ctx(id: string): CanvasRenderingContext2D | null {
  const el = document.getElementById(id) as HTMLCanvasElement | null;
  return el?.getContext("2d") ?? null;
}

// ── 차트 렌더 함수 ────────────────────────────────────────────────────────

/** 추이 라인 차트 (방문자 + 전환) */
function renderTrend(report: AnalyticsBootstrap): void {
  const c = ctx("analytics-trend");
  const t = report.trend;
  if (!c || !t) return;
  const base = { tension: 0.35, borderWidth: 2, pointRadius: 0, pointHoverRadius: 4, pointHitRadius: 12 };
  new Chart(c, {
    type: "line",
    data: {
      labels: t.labels,
      datasets: [
        { label: "방문자",  data: t.visitors,    borderColor: PALETTE[0], backgroundColor: PALETTE[0] + "1f", fill: true, ...base },
        { label: "문의 전환", data: t.conversions, borderColor: PALETTE[3], backgroundColor: "transparent", ...base },
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: { legend: { position: "bottom" } },
      scales: {
        x: { grid: { display: false } },
        y: { beginAtZero: true, ticks: { precision: 0 } },
      },
    },
  });
}

/** 채널 도넛 차트 (채널Perf 또는 legacy channels) */
function renderChannelDonut(report: AnalyticsBootstrap): void {
  const c = ctx("analytics-channel-donut") || ctx("analytics-channel");
  if (!c) return;

  let labels: string[] = [];
  let data:   number[] = [];
  let colors: string[] = [];

  // 색은 채널 의미색(direct/internal 이 회색) 대신 비비드 PALETTE 순환으로 — 다채로운 도넛
  if (report.channelPerf && report.channelPerf.length > 0) {
    labels = report.channelPerf.map(r => CHANNEL_LABELS[r.channel] || r.channel);
    data   = report.channelPerf.map(r => r.visitors);
    colors = report.channelPerf.map((_, i) => PALETTE[i % PALETTE.length]);
  } else if (report.channels && report.channels.length > 0) {
    labels = report.channels.map(r => CHANNEL_LABELS[r.channel] || r.channel);
    data   = report.channels.map(r => r.sessions);
    colors = report.channels.map((_, i) => PALETTE[i % PALETTE.length]);
  }
  if (data.length === 0) return;

  new Chart(c, {
    type: "doughnut",
    data: { labels, datasets: [{ data, backgroundColor: colors, borderWidth: 2, borderColor: "#fff" }] },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { position: "right" } },
      cutout: "62%",
    },
  });
}

/** 소스 가로막대 */
function renderSources(report: AnalyticsBootstrap): void {
  const c = ctx("analytics-source");
  const rows = report.sources || [];
  if (!c || rows.length === 0) return;
  new Chart(c, {
    type: "bar",
    data: {
      labels: rows.map(r => r.source),
      datasets: [{ label: "방문자", data: rows.map(r => r.sessions), backgroundColor: rows.map((_, i) => PALETTE[i % PALETTE.length]), borderRadius: 6, maxBarThickness: 26 }],
    },
    options: {
      indexAxis: "y", responsive: true, maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { grid: { display: false } } },
    },
  });
}

/** 전환 이벤트 가로막대 */
function renderConversions(report: AnalyticsBootstrap): void {
  const c = ctx("analytics-conversion");
  const rows = report.conversions || [];
  if (!c || rows.length === 0) return;
  new Chart(c, {
    type: "bar",
    data: {
      labels: rows.map(r => r.event_label || r.event_name || "?"),
      datasets: [{ label: "건수", data: rows.map(r => Number(r.count || 0)), backgroundColor: rows.map((_, i) => PALETTE[i % PALETTE.length]), borderRadius: 6, maxBarThickness: 26 }],
    },
    options: {
      indexAxis: "y", responsive: true, maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { grid: { display: false } } },
    },
  });
}

/** 신규/재방문 도넛 (중앙 "신규 NN%" 텍스트 포함) */
function renderNewReturn(audKpis: NewReturnData): void {
  const c = ctx("analytics-new-return");
  if (!c) return;
  const newV = Number(audKpis.new_visitors || 0);
  const ret  = Number(audKpis.returning     || 0);
  if (newV + ret === 0) return;
  const newPct = Math.round(newV / (newV + ret) * 100);

  const centerTextPlugin = {
    id: "centerText",
    afterDraw(chart: any) {
      const { ctx: cx, chartArea: { top, left, width, height } } = chart;
      const cxPos = left + width  / 2;
      const cyPos = top  + height / 2;
      cx.save();
      cx.textAlign    = "center";
      cx.textBaseline = "middle";
      cx.fillStyle    = "#374151";
      cx.font         = "600 1.4rem Pretendard, 'Apple SD Gothic Neo', sans-serif";
      cx.fillText("신규", cxPos, cyPos - 9);
      cx.font         = "700 2rem Pretendard, 'Apple SD Gothic Neo', sans-serif";
      cx.fillStyle    = PALETTE[0];
      cx.fillText(`${newPct}%`, cxPos, cyPos + 11);
      cx.restore();
    },
  };

  new Chart(c, {
    type: "doughnut",
    data: {
      labels: ["신규 방문자", "재방문자"],
      datasets: [{ data: [newV, ret], backgroundColor: [PALETTE[0], PALETTE[1]], borderWidth: 2, borderColor: "#fff" }],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (ctx: any) => ` ${ctx.label}: ${ctx.parsed}명` } },
      },
      cutout: "60%",
    },
    plugins: [centerTextPlugin],
  });
}

/** 디바이스/브라우저/OS — 범용 도넛 또는 막대 */
function renderBreakdown(
  canvasId: string,
  rows: BreakdownRow[],
  key: string,
  labelMap?: Record<string, string>,
  type: "doughnut" | "bar" = "doughnut",
): void {
  const c = ctx(canvasId);
  if (!c || !rows?.length) return;
  const labels = rows.map(r => (labelMap?.[String(r[key])]) || String(r[key]));
  const data   = rows.map(r => r.sessions);
  if (type === "bar") {
    new Chart(c, {
      type: "bar",
      data: { labels, datasets: [{ label: "세션", data, backgroundColor: rows.map((_, i) => PALETTE[i % PALETTE.length]), borderRadius: 6, maxBarThickness: 36 }] },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
    });
  } else {
    new Chart(c, {
      type: "doughnut",
      data: { labels, datasets: [{ data, backgroundColor: rows.map((_, i) => PALETTE[i % PALETTE.length]), borderWidth: 2, borderColor: "#fff" }] },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "right" } }, cutout: "62%" },
    });
  }
}

// ── 진입점 ────────────────────────────────────────────────────────────────
// 디스패치 키 = 라우트 메서드명. 관리자 레이아웃의 app.js <script data-action>
// 이 frontend_controller()(=라우트 메서드명)로 채워지고, PageApplication 은
// 그 값을 우선 읽는다. 따라서 아래 export 키는 routes/admin.php 의
// analytics.* 메서드명(index·acquisition·pages·audience·marketingInsights·developer)
// 과 정확히 1:1 이어야 한다. (뷰 파일의 data-action 속성은 무시됨.)
export const AdminAnalyticsController = {

  /** 성과 대시보드 (라우트: index) */
  index(): void {
    Logger.debug(`${CTRL}.index`);
    if (typeof Chart === "undefined") return;
    const report = readBootstrap();
    if (!report) return;
    applyDefaults();
    try { renderTrend(report);        } catch (e) { Logger.debug(`trend: ${(e as Error).message}`); }
    try { renderChannelDonut(report); } catch (e) { Logger.debug(`channel: ${(e as Error).message}`); }
    try { renderConversions(report);  } catch (e) { Logger.debug(`conv: ${(e as Error).message}`); }
  },

  /** 유입 분석 (라우트: acquisition) */
  acquisition(): void {
    Logger.debug(`${CTRL}.acquisition`);
    if (typeof Chart === "undefined") return;
    const report = readBootstrap();
    if (!report) return;
    applyDefaults();
    try { renderChannelDonut(report); } catch (e) { Logger.debug(`channel: ${(e as Error).message}`); }
    try { renderSources(report);      } catch (e) { Logger.debug(`sources: ${(e as Error).message}`); }
  },

  /** 페이지 성과 (라우트: pages) — 표 전용, 차트 캔버스 없음 */
  pages(): void {
    Logger.debug(`${CTRL}.pages`);
    // 차트 없음 — 디스패치 경고 방지용 no-op.
  },

  /** 고객 분석 (라우트: audience) */
  audience(): void {
    Logger.debug(`${CTRL}.audience`);
    if (typeof Chart === "undefined") return;
    const report = readBootstrap();
    if (!report) return;
    applyDefaults();

    // 신규/재방문 도넛 — bootstrap에 없으므로 DOM에서 읽는다
    const audEl = document.querySelector<HTMLElement>("[data-aud-new]");
    if (audEl) {
      const newV = parseInt(audEl.dataset.audNew || "0", 10);
      const ret  = parseInt(audEl.dataset.audRet  || "0", 10);
      try { renderNewReturn({ new_visitors: newV, returning: ret }); }
      catch (e) { Logger.debug(`new-return: ${(e as Error).message}`); }
    }

    try { renderBreakdown("analytics-device",  report.devices  || [], "device_type", DEVICE_LABELS, "doughnut"); }
    catch (e) { Logger.debug(`device: ${(e as Error).message}`); }
    try { renderBreakdown("analytics-browser", report.browsers || [], "browser",     undefined,     "bar"); }
    catch (e) { Logger.debug(`browser: ${(e as Error).message}`); }
  },

  /** 마케팅 인사이트 (라우트: marketingInsights) */
  marketingInsights(): void {
    Logger.debug(`${CTRL}.marketingInsights`);
    if (typeof Chart === "undefined") return;
    const report = readBootstrap();
    if (!report) return;
    applyDefaults();
    try { renderTrend(report);        } catch (e) { Logger.debug(`trend: ${(e as Error).message}`); }
    try { renderChannelDonut(report); } catch (e) { Logger.debug(`channel: ${(e as Error).message}`); }
    try { renderConversions(report);  } catch (e) { Logger.debug(`conv: ${(e as Error).message}`); }
  },

  /** 개발자 데이터 (라우트: developer) — 전체 차트 */
  developer(): void {
    Logger.debug(`${CTRL}.developer`);
    if (typeof Chart === "undefined") return;
    const report = readBootstrap();
    if (!report) return;
    applyDefaults();
    try { renderTrend(report); }        catch (e) { Logger.debug(`trend: ${(e as Error).message}`); }
    try { renderChannelDonut(report); } catch (e) { Logger.debug(`channel: ${(e as Error).message}`); }
    try { renderSources(report); }      catch (e) { Logger.debug(`sources: ${(e as Error).message}`); }
    try { renderBreakdown("analytics-device",  report.devices  || [], "device_type", DEVICE_LABELS, "doughnut"); }
    catch (e) { Logger.debug(`device: ${(e as Error).message}`); }
    try { renderBreakdown("analytics-browser", report.browsers || [], "browser",     undefined,     "bar"); }
    catch (e) { Logger.debug(`browser: ${(e as Error).message}`); }
    try { renderBreakdown("analytics-os",      report.os       || [], "os",          undefined,     "bar"); }
    catch (e) { Logger.debug(`os: ${(e as Error).message}`); }
    try { renderConversions(report); }  catch (e) { Logger.debug(`conv: ${(e as Error).message}`); }
  },
};
